import { assertUnreachable } from '@disy/lib/custom-error';
import { getSetting } from '@disy/workbook.lib/settings';

import { isEmbeddedView } from 'cadenza/integration/cadenza-api-util';
import { getStore } from 'cadenza/store/cadenza-store';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import type { View } from 'cadenza/workbook/view/view';
import { isDetailView } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { GRID_SIZE, getGridArea } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

// minimum height with title bar when having top and bottom description
const MIN_HEIGHT_TO_SEE_FULL_INDICATOR_PIXELS = 120;
// below roughly this width, there are only two letters of the title left
const MIN_WIDTH_TO_READ_TITLE_PIXELS = 200;

export function isViewSmall(view: View) {
  let width = view.clientWidth;
  let height = view.clientHeight;
  if (!view.grid) {
    return width < MIN_WIDTH_TO_READ_TITLE_PIXELS || height < MIN_HEIGHT_TO_SEE_FULL_INDICATOR_PIXELS;
  }
  const gridRect = view.grid.getBoundingClientRect();
  const [rowStart, colStart, rowEnd, colEnd] = getGridArea(view, gridRect.width, gridRect.height);
  if (!isNaN(rowStart)) {
    const viewGridWidth = 0.5 + colEnd - colStart;
    const viewGridHeight = 0.5 + rowEnd - rowStart;
    const columnWidth = gridRect.width / GRID_SIZE;
    const rowHeight = gridRect.height / GRID_SIZE;
    width = viewGridWidth * columnWidth;
    height = viewGridHeight * rowHeight;
  }
  return width < MIN_WIDTH_TO_READ_TITLE_PIXELS || height < MIN_HEIGHT_TO_SEE_FULL_INDICATOR_PIXELS;
}

export function titlebarVisibilitySetting(view: View) {
  const titleBarKey = CommonViewSettingsKey.toPersistenceKey('TITLEBAR_VISIBILITY');
  return getSetting<'auto' | 'off'>(view, titleBarKey) || 'auto';
}

export function isTitleBarVisible(view: View, viewIsSmall: boolean) {
  const titleBarVisibility = titlebarVisibilitySetting(view);
  switch (titleBarVisibility) {
    case 'auto':
      return !viewIsSmall;
    case 'off':
      return isDetailView(getStore().getState(), view.viewId);
    default:
      assertUnreachable(titleBarVisibility);
  }
}

export function isViewActionToolbarShown() {
  return (
    isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-titlebar-buttons') && // if partial view mode hide all the toolbar buttons regardless of previous settings
    !isEmbeddedView() // for embedding view we do not show any buttons (does not apply to embedding worksheets, workbooks)
  );
}
